import React, { type ReactNode } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown, Inbox } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from './EmptyState'
import { Spinner } from './Spinner'

export interface Column<T> {
  key: string
  header: string
  render?: (item: T) => ReactNode
  className?: string
  sortable?: boolean
  hideBelow?: 'sm' | 'md' | 'lg'
}

function getResponsiveClass(hideBelow?: 'sm' | 'md' | 'lg'): string {
  if (!hideBelow) return ''
  const map = { sm: 'hidden sm:table-cell', md: 'hidden md:table-cell', lg: 'hidden lg:table-cell' }
  return map[hideBelow]
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (item: T) => string | number
  loading?: boolean
  emptyMessage?: string
  onRowClick?: (item: T) => void
  className?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (key: string) => void
  /** Table density. 'md' (default) is unstyled — byte-identical to pre-P008 output. */
  size?: 'xs' | 'sm' | 'md'
}

const SIZE_CLASSES: Record<NonNullable<DataTableProps<unknown>['size']>, string> = {
  xs: 'table-xs',
  sm: 'table-sm',
  md: '',
}

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  loading,
  emptyMessage,
  onRowClick,
  className = '',
  sortBy,
  sortOrder,
  onSort,
  size = 'md',
}: DataTableProps<T>) {
  const { t } = useTranslation('common')
  const empty = emptyMessage ?? t('empty.no_data')

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="xl" className="text-primary" />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="w-12 h-12" />}
        title={empty}
      />
    )
  }

  const renderSortIcon = (column: Column<T>) => {
    if (!column.sortable) return null
    if (sortBy !== column.key) {
      return <ChevronsUpDown className="w-3 h-3 opacity-40" />
    }
    return sortOrder === 'asc'
      ? <ChevronUp className="w-3 h-3" />
      : <ChevronDown className="w-3 h-3" />
  }

  // Derive aria-sort attribute value for a sortable column
  const getAriaSort = (column: Column<T>): React.AriaAttributes['aria-sort'] | undefined => {
    if (!column.sortable) return undefined
    if (sortBy !== column.key) return 'none'
    return sortOrder === 'asc' ? 'ascending' : 'descending'
  }

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className={`table lg:min-w-max ${SIZE_CLASSES[size]}`.trim()}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                aria-sort={getAriaSort(column)}
                className={`${getResponsiveClass(column.hideBelow)} ${column.className || ''} ${column.sortable ? 'cursor-pointer select-none hover:bg-base-200' : ''}`}
                onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
              >
                {column.sortable && onSort ? (
                  // Keyboard-activable wrapper for sortable headers.
                  // role="button" + tabIndex={0} makes the header reachable via Tab
                  // and activable via Enter or Space (standard button semantics).
                  <span
                    role="button"
                    tabIndex={0}
                    className="inline-flex items-center gap-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onSort(column.key)
                      }
                    }}
                  >
                    {column.header}
                    {renderSortIcon(column)}
                  </span>
                ) : (
                  <span className={column.sortable ? 'inline-flex items-center gap-1' : ''}>
                    {column.header}
                    {renderSortIcon(column)}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr
              key={keyExtractor(item)}
              className={onRowClick ? 'hover cursor-pointer' : 'hover'}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map((column) => (
                <td key={column.key} className={`${getResponsiveClass(column.hideBelow)} ${column.className || ''}`}>
                  {column.render
                    ? column.render(item)
                    : (item as Record<string, unknown>)[column.key] as ReactNode}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
